import type { Country, Currency } from '../graphql/generated/graphql';

/**
 * ISO 4217 codes that should bubble to the top of currency selectors. Order in
 * this array is the visual order — extend / replace per market when needed.
 */
export const PREFERRED_CURRENCY_CODES: readonly string[] = ['CZK', 'USD', 'EUR'];

/**
 * ISO 3166-1 alpha-2 codes that should bubble to the top of country selectors.
 * Order in this array is the visual order.
 */
export const PREFERRED_COUNTRY_CODES: readonly string[] = ['CZ', 'SK', 'DE'];

const CURRENCY_RANK = buildRank(PREFERRED_CURRENCY_CODES);
const COUNTRY_RANK = buildRank(PREFERRED_COUNTRY_CODES);

function buildRank(codes: readonly string[]): ReadonlyMap<string, number> {
  return new Map(codes.map((code, idx) => [code, idx]));
}

function sortByRank<T extends { code?: string | null }>(
  items: readonly T[],
  rank: ReadonlyMap<string, number>
): readonly T[] {
  return [...items].sort((a, b) => {
    const ra = rank.get(a.code?.toUpperCase() ?? '');
    const rb = rank.get(b.code?.toUpperCase() ?? '');
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return 0;
  });
}

/** True when the currency belongs to the preferred set. */
export function isPreferredCurrency(code: string | null | undefined): boolean {
  if (!code) return false;
  return CURRENCY_RANK.has(code.trim().toUpperCase());
}

/** True when the country belongs to the preferred set. */
export function isPreferredCountry(code: string | null | undefined): boolean {
  if (!code) return false;
  return COUNTRY_RANK.has(code.trim().toUpperCase());
}

/**
 * Returns a copy of the list with preferred currencies first (in
 * `PREFERRED_CURRENCY_CODES` order), followed by the remaining currencies in
 * their original order.
 */
export function sortCurrenciesByPreferred(currencies: readonly Currency[]): readonly Currency[] {
  return sortByRank(currencies, CURRENCY_RANK);
}

/**
 * Returns a copy of the list with preferred countries first (in
 * `PREFERRED_COUNTRY_CODES` order), followed by the remaining countries in
 * their original order.
 */
export function sortCountriesByPreferred(countries: readonly Country[]): readonly Country[] {
  return sortByRank(countries, COUNTRY_RANK);
}
